'use client';

import { logout } from './api';
import { Wordmark } from './components';

export interface RailItem {
  key: string;
  label: string;
  /** Link for route-based navigation; omit for in-page tabs. */
  href?: string;
  onSelect?: () => void;
  /**
   * How many things behind this link are waiting to be read. Zero and undefined
   * both draw nothing: a badge showing 0 is a mark that cannot be cleared, and
   * the eye keeps returning to it.
   */
  badge?: number;
  /**
   * A plain unread mark — no count, just "something here needs you" — for a
   * link that has no number to show (an incomplete form, not a queue).
   * Ignored when `badge` is also set: a link does not carry two marks.
   */
  dot?: boolean;
  /** Something behind this link is running right now (a deploy): a slow spinner. */
  busy?: boolean;
  /**
   * A small mark before the label, `currentColor`-stroked so it picks up
   * whatever colour the link itself is drawn in (dim at rest, white on
   * hover or when active) without a second rule to keep in step with it.
   * Optional: a group nobody has iconed yet just draws its label flush left,
   * same as before this existed.
   */
  icon?: React.ReactNode;
  /**
   * Overrides the icon's colour, at full opacity, in every state: a fixed
   * CSS colour (a `--rail-accent-*` token in practice) rather than letting
   * it ride on the link's own dim/hover/active colour. One per nav group,
   * set once in nav.tsx, so "which shelf is this" is answerable by colour
   * before a reader gets as far as the label.
   */
  iconColor?: string;
  /** Somewhere this app does not route, opened in a new tab. */
  external?: boolean;
  /** Drawn as a filled button rather than a row: the one action a section exists to offer. */
  prominent?: boolean;
}

/**
 * A named run of links, always visible.
 *
 * One level only (a group holds items, never other groups) because the
 * rail this exists for has two kinds of destination and no third: a page, or
 * a labelled shelf of pages. Nesting further would answer a question nobody
 * asked. The label is a heading, not a control: a support panel with twenty
 * destinations is a map somebody has to learn, and a map with parts hidden
 * behind a click is one they learn slower, not faster.
 */
export interface RailGroup {
  key: string;
  label: string;
  items: RailItem[];
}

export type RailEntry = RailItem | RailGroup;

function isGroup(entry: RailEntry): entry is RailGroup {
  return 'items' in entry;
}

/**
 * The navigation rail, shared by both apps.
 *
 * It takes items rather than owning routes, because the client app navigates
 * between pages and the support app switches queues in place, same furniture,
 * different wiring.
 *
 * Entries may be a bare link or a {@link RailGroup}. A short rail (the client
 * app's) never needs the latter; the support panel's grew past the point
 * where every destination could sit in one flat run and still be found by
 * eye, which is the only reason groups exist at all.
 */
export function Rail({
  context,
  brand,
  person,
  items,
  activeKey,
  topExtra,
  footer,
  onSignOut,
  linkComponent: Link,
}: {
  context: string;
  /**
   * The product name above the links, or null where it must not be shipped,
   * see {@link Wordmark}. No default: a default is how it ends up in an app
   * that was supposed to stay anonymous.
   */
  brand: string | null;
  /**
   * Who is signed in, which is the question the top of a rail is actually asked
   * in an app where accounts are named and some of them are test accounts. It
   * arrives after the session does, so the head keeps its height either way.
   */
  /**
   * `role` is omitted where every account in the app is the same role: the
   * support panel tells Admin from Developer apart, the client app has
   * nothing to say by showing "Client" on every signed-in person in it.
   */
  person?: { name: string | null; email?: string | null; role?: string } | null;
  items: RailEntry[];
  activeKey: string | null;
  /**
   * Sits right under the signed-in-user block, above every nav item, for
   * something a reader should see before they start scanning the map, not
   * after (that's {@link footer} below). One caller so far: the admin
   * panel's language switch, which stays out of the way of the twenty
   * destinations under it precisely by not being one of them.
   */
  topExtra?: React.ReactNode;
  footer?: React.ReactNode;
  onSignOut: () => void;
  /**
   * The app's router link (next/link). Deliberately untyped: the rail only
   * renders it with href/className/aria-current, and next/link's own prop
   * types are wider than that, so pinning a shape here would reject the very
   * component this prop exists to accept.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkComponent?: React.ComponentType<any>;
}) {
  const renderLink = (item: RailItem) => {
    const className = item.prominent ? 'rail-link rail-cta' : 'rail-link';
    const current = item.key === activeKey ? ('page' as const) : undefined;
    // Icon and label stay grouped on the left so the badge (the other child
    // of this flex row) is the only thing space-between pushes to the right.
    const label = (
      <span key="label" className="rail-link-body">
        {item.icon && (
          <span
            className={`rail-icon${item.iconColor ? ' has-accent' : ''}`}
            style={item.iconColor ? { color: item.iconColor } : undefined}
            aria-hidden
          >
            {item.icon}
          </span>
        )}
        {item.label}
      </span>
    );
    const content = item.badge
      ? [label, <span key="badge" className="rail-badge">{item.badge}</span>]
      : item.busy
        ? [label, <span key="busy" className="statusspin rail-spin" aria-hidden />]
        : item.dot
          ? [label, <span key="dot" className="rail-dot" aria-hidden />]
          : label;
    const described = item.badge
      ? `${item.label}, ${item.badge} unread`
      : item.busy
        ? `${item.label}, in progress`
        : item.dot
        ? `${item.label}, needs your details`
        : undefined;

    if (item.href && item.external) {
      return (
        <a key={item.key} data-rail-key={item.key} href={item.href} className={className} target="_blank" rel="noreferrer" aria-label={described}>
          {content}
        </a>
      );
    }

    if (item.href && Link) {
      return (
        <Link key={item.key} data-rail-key={item.key} href={item.href} className={className} aria-current={current} aria-label={described}>
          {content}
        </Link>
      );
    }

    return (
      <button
        key={item.key}
        data-rail-key={item.key}
        type="button"
        className={`${className} rail-button`}
        aria-current={current}
        aria-label={described}
        onClick={item.onSelect}
      >
        {content}
      </button>
    );
  };

  return (
    <nav className="rail">
      {/* The scroll lives here, not on the sticky nav. A sticky element that
          is also a scroll container shifts every child a pixel on each frame
          of the page scroll, then puts them back when the scroll stops. */}
      <div className="rail-scroll">
      <div className="rail-brand">
        {/* Who is signed in, read at a glance: an initials avatar with a
            presence dot, the name and full email stacked beside it, and the
            role as a pill on the right. */}
        {person ? (
          <div className="rail-user">
            <span className="rail-avatar" aria-hidden>
              {(person.name?.trim()?.[0] ?? person.email?.[0] ?? '?').toUpperCase()}
              <span className="rail-presence" />
            </span>
            <span className="rail-user-id">
              <strong className="rail-user-name">{person.name?.trim() || 'Signed in'}</strong>
              {person.email && <span className="rail-user-email">{person.email}</span>}
            </span>
            {person.role && (
              <span className="rail-role">
                {person.role.charAt(0) + person.role.slice(1).toLowerCase()}
              </span>
            )}
          </div>
        ) : brand ? (
          <Wordmark context={context} name={brand} />
        ) : (
          // An app with no name to show says nothing while the session is on
          // its way, but it holds the exact shape of what is coming: same
          // avatar, same two lines. Otherwise the links below step down the
          // moment the name arrives.
          <div className="rail-user is-waiting" aria-hidden>
            <span className="rail-avatar" />
            <span className="rail-user-id">
              <strong className="rail-user-name">&nbsp;</strong>
              <span className="rail-user-email">&nbsp;</span>
            </span>
          </div>
        )}
      </div>

      {topExtra}

      {items.map((entry) => {
        if (!isGroup(entry)) return renderLink(entry);

        return (
          <div key={entry.key} className="rail-group-block">
            <div className="rail-group" role="heading" aria-level={2}>
              {entry.label}
            </div>
            <div className="rail-group-items">{entry.items.map(renderLink)}</div>
          </div>
        );
      })}

      {/* Sign out belongs with the things you click, not in the footer where it
          was a footnote under whatever else lived down there. It reads as the
          last item of the list, so a rule above it and a colour on hover say it
          is the one that ends the session rather than opens a page. */}
      <div className="rail-signout-wrap">
        <button
          type="button"
          className="rail-link rail-signout"
          onClick={async () => {
            await logout();
            onSignOut();
          }}
        >
          Sign out
        </button>
      </div>

      {footer && <div className="rail-foot">{footer}</div>}
      </div>
    </nav>
  );
}
